import { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Button, Header, useToast } from '@aimsos/ui';
import { useStickyScroll } from '@aimsos/hooks';

import { useSaveWidget } from '../hooks/useSaveWidget.js';
import { isDataSourceLockedError } from '../utils/savedWidgetLock.js';
import { useSourceColumns } from '../hooks/useSourceColumns.js';
import { usePublishedDataset } from '../hooks/usePublishedDataset.js';
import { useWidgetBuilderForm } from '../hooks/useWidgetBuilderForm.js';
import { useWidgetTypes } from '../hooks/useWidgetTypes.js';
import ConfigureTab from './ConfigureTab.jsx';
import DataStep from './DataStep.jsx';
import DiscardChangesModal from './DiscardChangesModal.jsx';
import LivePreviewPane from './LivePreviewPane.jsx';
import SaveToCatalogModal from './SaveToCatalogModal.jsx';
import WidgetBuilderNavFooter from './WidgetBuilderNavFooter.jsx';
import WidgetBuilderTabs from './WidgetBuilderTabs.jsx';
import WidgetPlaygroundLayout from './WidgetPlaygroundLayout.jsx';

/**
 * The Widget Playground (CT-5190, filled in by CT-5191..CT-5200).
 *
 * **One tree for both modes.** Create and edit differ in three strings, one
 * hydration source and where `Save` posts — not in structure — so `mode` is a
 * prop rather than two components that drift apart.
 *
 * After a successful create, `Keep editing` keeps the user here and flips the
 * surface into edit mode against the id the service returned, so the next save
 * updates rather than creating a second widget.
 *
 * The AI `DESCRIBE IT` / `Generate` panel from the prototype is deliberately not
 * rendered: it is out of V1 per CT-5183, and a disabled stub would still be in
 * the DOM.
 */
function WidgetPlayground({ mode, initialWidget, onNewWidget }) {
  const { t } = useTranslation('dashboards');
  const navigate = useNavigate();
  const toast = useToast();

  const form = useWidgetBuilderForm(initialWidget);
  const {
    activeTab,
    goToTab,
    canSave,
    saveBlockedReason,
    saveLabelKey,
    isDirty,
    markSaved,
  } = form;

  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [savedWidget, setSavedWidget] = useState(null);

  const scrollRef = useRef(null);
  const { isScrolled } = useStickyScroll(scrollRef);

  const isConfigureTab = activeTab === 'configure';

  // Loaded here rather than inside the Data tab so the live preview reads the
  // same column metadata without a second request. The hook no-ops until a
  // source is chosen.
  const sourceColumns = useSourceColumns({
    sourceKind: form.dataset.sourceType || undefined,
    sourceId: form.dataset.sourceId || undefined,
    // The composite's field mapping, when it has one, decides the columns.
    compositeEntityUuid: form.dataset.compositeEntityUuid || undefined,
  });

  // A widget saved in this session is edited from here on, not created again.
  const widgetId = initialWidget?.id ?? savedWidget?.id;
  const isEdit = mode === 'edit' || Boolean(savedWidget);

  // Loaded here, not in the type picker: the picker renders it, the Configure
  // tab reads the selected type's binding slots from it, and the save sends
  // them — one list, one request.
  const widgetTypes = useWidgetTypes();
  // The catalog entry for the chosen type, resolved once: the Data step reads
  // its grouping rules from it, the Configure step its binding slots, and the
  // save its schema.
  const selectedType = useMemo(
    () => widgetTypes.types.find((entry) => entry.code === form.type) ?? null,
    [form.type, widgetTypes.types],
  );

  /*
    The Data step's selection is published as a DATASET before the widget is
    written on top of it (CT-5725). Asked for when the author moves on to
    Configure, and again right before a save — `/dataset/create` is
    idempotent, so the second ask is free unless the selection moved.
  */
  const publishedDataset = usePublishedDataset({ form, columns: sourceColumns.columns });

  const { save, isSaving, errorRef: saveErrorRef } = useSaveWidget({
    widgetId,
    columns: sourceColumns.columns,
    typeSchema: selectedType?.configSchema ?? null,
    // The Data step only counts rows, so the number's honest name is just
    // that. Persisted on the binding, which is what stops the widget from
    // rendering the query alias `record_count` (CT-5579). The day a measure
    // picker lands this becomes the measure's own field name.
    aggregateLabel: t('widgetBuilder.aggregate.recordCount'),
  });

  // What the live preview executes against. The stamp moves on every write, so
  // saving is what re-runs the query instead of leaving the previous numbers
  // under a definition that changed. `savedWidget` first: it is the newer of
  // the two once a save has happened without leaving the builder.
  const previewVersion = savedWidget?.versionStamp ?? initialWidget?.versionStamp;

  const leaveToCatalog = useCallback(() => {
    // Relative: the domain is mounted at `widgets/*` in the shell and at
    // `dashboards/widgets/*` standalone. `..` from `new` or `:id/edit` is the
    // catalog in both.
    navigate(mode === 'edit' ? '../..' : '..', { relative: 'path' });
  }, [mode, navigate]);

  const handleCancel = useCallback(() => {
    if (isDirty) {
      setIsDiscardOpen(true);
      return;
    }
    leaveToCatalog();
  }, [isDirty, leaveToCatalog]);

  const handleKeepEditing = useCallback(() => setIsDiscardOpen(false), []);

  const handleContinue = useCallback(async () => {
    // A selection that cannot be published is not configurable: the bindings
    // name the dataset's columns, so the author stays here with the reason.
    // The refusal comes back from the call itself: `publishedDataset.error`
    // is the previous render's state, so reading it here let a selection that
    // had *just* failed walk on to Configure and fail again at save, with a
    // generic toast instead of the reason.
    const { error } = await publishedDataset.ensureDataset();
    if (error) return;
    goToTab('configure');
  }, [goToTab, publishedDataset]);
  const handleBackToData = useCallback(() => goToTab('data'), [goToTab]);

  const handleDiscard = useCallback(() => {
    setIsDiscardOpen(false);
    leaveToCatalog();
  }, [leaveToCatalog]);

  /**
   * Why the save failed, in the author's terms.
   *
   * One rejection is not a failure to retry: a saved widget keeps the dataset
   * it was created from, so changing the source, the columns or the filters
   * is refused and the only way forward is a new widget (CT-5641). Saying
   * "something went wrong, please try again" there sends the author round a
   * loop that cannot end.
   */
  const showSaveErrorToast = useCallback(() => {
    const locked = isDataSourceLockedError(saveErrorRef.current);
    const key = locked ? 'dataSourceLockedToast' : 'saveErrorToast';
    toast[locked ? 'warning' : 'error']({
      title: t(`widgetBuilder.${key}.title`),
      message: t(`widgetBuilder.${key}.message`),
    });
  }, [saveErrorRef, t, toast]);

  /**
   * The draft saved but the ride-along publish was rejected (`DSW012` and
   * friends) — `useSaveWidget` carries the reason back on `publishError`
   * rather than throwing, precisely so this can be told apart from a failed
   * save. Without this, the success toast below fires unconditionally and the
   * widget is left in `DRAFT` with nothing on screen saying so.
   */
  const showPublishWarningToast = useCallback((publishError) => {
    toast.warning({
      title: t('widgetBuilder.notPublishedToast.title'),
      message: publishError?.message || t('widgetBuilder.notPublishedToast.message'),
    });
  }, [t, toast]);

  /** Edit mode saves straight away; creating asks first. */
  const handleSaveClick = useCallback(async () => {
    if (!isEdit) {
      setIsConfirmOpen(true);
      return;
    }

    const { dataset: datasetRef, error } = await publishedDataset.ensureDataset();
    // Saving without the dataset falls back to `ENTITY`, whose bindings the
    // Data step no longer feeds — the backend answers `UI_BINDING_REQUIRED`
    // and the author never learns the source was the thing that failed.
    if (error) {
      goToTab('data');
      return;
    }
    const saved = await save({ ...form, datasetRef });
    if (!saved) {
      showSaveErrorToast();
      return;
    }
    markSaved();
    if (saved.publishError) {
      // Stays on the page (no `leaveToCatalog`), so the new `versionStamp`
      // has to be adopted here too — otherwise `previewVersion` keeps the
      // pre-save stamp and the live preview shows stale data instead of
      // re-running against what was just written.
      setSavedWidget(saved);
      showPublishWarningToast(saved.publishError);
      return;
    }
    toast.success({
      title: t('widgetBuilder.savedToast.title'),
      message: t('widgetBuilder.savedToast.message', { name: saved.name }),
    });
    leaveToCatalog();
  }, [
    form, goToTab, isEdit, leaveToCatalog, markSaved, publishedDataset, save,
    showPublishWarningToast, showSaveErrorToast, t, toast,
  ]);

  /**
   * Save, then go where the dialog's button said.
   *
   * The prototype folds the confirm and the post-save choice into one modal, so
   * the destination is decided before the write rather than after it.
   */
  const saveAndThen = useCallback(
    async (destination) => {
      // Captured before the write: `isEdit` flips true the moment a create
      // succeeds (`savedWidget` gets set below, for `keep-editing`), so this
      // is the only point that still knows whether the widget existed before
      // this call — which decides "created" vs "saved" wording below.
      const wasEdit = isEdit;
      const { dataset: datasetRef, error } = await publishedDataset.ensureDataset();
      if (error) {
        setIsConfirmOpen(false);
        goToTab('data');
        return;
      }
      const saved = await save({ ...form, datasetRef });
      // On failure the dialog stays open (every form value intact) and a
      // toast — not a message baked into the modal — carries the error, so
      // it reads as a transient thing to retry rather than a permanent state
      // of the dialog itself.
      if (!saved) {
        showSaveErrorToast();
        return;
      }

      // Re-baseline first: from here the widget is saved, so `Cancel` must not
      // claim there is unsaved work.
      markSaved();
      setIsConfirmOpen(false);

      if (saved.publishError) {
        // Adopted regardless of `destination`: the widget now has an id, and
        // the next save must be an update of it, not a second create. Staying
        // here (no navigation) is deliberate too — the same reasoning as the
        // failure branch above applies to a toast the user still needs to read.
        setSavedWidget(saved);
        showPublishWarningToast(saved.publishError);
        return;
      }
      toast.success(
        wasEdit
          ? {
            title: t('widgetBuilder.savedToast.title'),
            message: t('widgetBuilder.savedToast.message', { name: saved.name }),
          }
          : {
            title: t('widgetBuilder.createdToast.title'),
            message: t('widgetBuilder.createdToast.message', { name: saved.name }),
          },
      );

      if (destination === 'catalog') {
        leaveToCatalog();
        return;
      }
      if (destination === 'new') {
        setSavedWidget(null);
        onNewWidget?.();
        return;
      }
      // `keep-editing`: stay here, now editing the widget the service returned.
      setSavedWidget(saved);
    },
    [
      form, goToTab, isEdit, leaveToCatalog, markSaved, onNewWidget, publishedDataset, save,
      showPublishWarningToast, showSaveErrorToast, t, toast,
    ],
  );

  const handleSaveAndClose = useCallback(() => saveAndThen('catalog'), [saveAndThen]);
  const handleSaveAndNew = useCallback(() => saveAndThen('new'), [saveAndThen]);
  const handleSaveAndKeepEditing = useCallback(() => saveAndThen('keep-editing'), [saveAndThen]);

  const handleCancelSave = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  /*
    Saving before the columns are in hand writes a different widget.

    Each exposed column's alias is derived from the field's own code
    (`buildColumnAliases`), so with the metadata still loading — or failed —
    the same selection falls back to the positional `col_1`, `col_2`. On a
    create that is merely inconsistent; on an **edit** the rev4 backend
    refuses an update whose columns do not match the dataset the widget was
    created from, and the author is told to create a new widget for a change
    they did not make (CT-5641).
  */
  const columnsFailed = Boolean(sourceColumns.error);
  const columnsPending = sourceColumns.isLoading || columnsFailed;
  const saveBlocked = !canSave || columnsPending;
  /*
    Both states block, and they are not the same sentence. A request still in
    flight is worth waiting for; one that already failed is not, and telling
    the author to hold on leaves them waiting for something that ended — with
    the retry sitting on the Data tab, where the message now sends them.
  */
  const blockedTitle = (() => {
    if (saveBlockedReason) return t(saveBlockedReason);
    if (columnsFailed) return t('widgetBuilder.save.blockedColumnsFailed');
    return sourceColumns.isLoading ? t('widgetBuilder.save.blockedColumnsPending') : undefined;
  })();
  const widgetName = form.name.trim() || t('widgetBuilder.preview.untitled');
  const primaryLabel = isEdit && canSave ? t('widgetBuilder.edit.save') : t(saveLabelKey);

  // The header keeps the save so it is reachable from either tab — the
  // prototype shows `Save to catalog` top-right and again in the footer on
  // Configure. Both call the same handler.
  const headerActions = useMemo(
    () => (
      <Button
        variant="primary"
        // Disabled while saving too: `loading` alone kept it clickable.
        disabled={saveBlocked || isSaving}
        loading={isSaving}
        title={blockedTitle}
        onClick={handleSaveClick}
      >
        {primaryLabel}
      </Button>
    ),
    [saveBlocked, blockedTitle, isSaving, primaryLabel, handleSaveClick],
  );

  return (
    <main
      ref={scrollRef}
      className="scrollbar-aimsos-auto flex h-full min-h-0 flex-1 flex-col overflow-y-auto bg-surface-base"
    >
      {/* `px-6` only: the top padding belongs to the sticky header (it has to
          scroll under it, not above it), and a bottom one would sit under the
          `sticky bottom-0` nav footer and vanish once it pins — the bar would
          land 24px higher on a short tab than on a long one. */}
      <div className="flex min-h-0 flex-1 flex-col gap-6 px-6">
        {/* Pinned, with the backdrop only once the page has moved — Agentic
            Studio's networks list, down to the `-mx-6 px-6` that runs the
            backdrop edge to edge over this column's own padding. */}
        <div className="sticky top-0 z-20 -mx-6 px-6">
          {/* The vertical padding belongs to the painted element, never to the
              sticky wrapper: outside it the backdrop stops at the header's own
              box and content scrolls visibly through the gutter. */}
          <div
            className={[
              'pt-6 transition-colors duration-200',
              isScrolled ? 'bg-surface-base' : '',
            ].filter(Boolean).join(' ')}
          >
            <Header
              size={isScrolled ? 'compress' : 'M'}
              title={
                isEdit
                  ? t('widgetBuilder.edit.title', { name: initialWidget?.name ?? savedWidget?.name ?? '' })
                  : t('widgetBuilder.create.title')
              }
              showBackButton={false}
              showDescription={!isScrolled}
              description={isEdit ? t('widgetBuilder.edit.subtitle') : t('widgetBuilder.create.subtitle')}
              showTag={false}
              primaryCta={headerActions}
            />
          </div>

          {/* Content has to leave somewhere — a hard edge makes the bar look
              like it clips the page instead of floating over it. */}
          {isScrolled ? (
            <div
              aria-hidden
              className="pointer-events-none h-10 bg-gradient-to-b from-surface-base to-transparent"
            />
          ) : null}
        </div>

        {/* Above the two columns, not inside the left one: with the tabs in the
            configuration column the preview card started level with them, a tab
            bar's height higher than the card it sits beside. The tabs belong to
            the page anyway — they switch both columns' content, not the left
            one's. */}
        <WidgetBuilderTabs activeTab={activeTab} onTabChange={goToTab} disabled={isSaving} />

        <WidgetPlaygroundLayout
          configuration={
            <>
              {activeTab === 'data' ? (
                <DataStep
                  form={form}
                  sourceColumns={sourceColumns}
                  datasetError={publishedDataset.error}
                />
              ) : null}

              {activeTab === 'configure' ? (
                <ConfigureTab
                  form={form}
                  widgetTypes={widgetTypes}
                  sourceColumns={sourceColumns}
                />
              ) : null}
            </>
          }
          preview={(
            <LivePreviewPane
              form={form}
              columns={sourceColumns.columns}
              widgetUuid={widgetId}
              version={previewVersion}
            />
          )}
        />

        {/* Outside the layout on purpose: the bar spans the whole page, under
            both columns, the way the library's paginator does. */}
        <WidgetBuilderNavFooter
          variant={isConfigureTab ? 'back-next' : 'cancel-next'}
          ariaLabel={t('widgetBuilder.navFooterAria')}
          backLabel={isConfigureTab ? t('widgetBuilder.back') : t('widgetBuilder.cancel')}
          nextLabel={isConfigureTab ? primaryLabel : t('widgetBuilder.continueToConfigure')}
          nextDisabled={isConfigureTab && saveBlocked}
          nextTitle={isConfigureTab ? blockedTitle : undefined}
          nextLoading={isConfigureTab ? isSaving : publishedDataset.isPreparing}
          busy={isSaving}
          onBack={isConfigureTab ? handleBackToData : handleCancel}
          onNext={isConfigureTab ? handleSaveClick : handleContinue}
        />
      </div>

      <DiscardChangesModal
        isOpen={isDiscardOpen}
        onKeepEditing={handleKeepEditing}
        onDiscard={handleDiscard}
      />

      <SaveToCatalogModal
        isOpen={isConfirmOpen}
        widgetName={widgetName}
        isSaving={isSaving}
        onCancel={handleCancelSave}
        onSaveAndClose={handleSaveAndClose}
        onSaveAndNew={handleSaveAndNew}
        onSaveAndKeepEditing={handleSaveAndKeepEditing}
      />
    </main>
  );
}

WidgetPlayground.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  initialWidget: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    versionStamp: PropTypes.string,
  }),
  /** Create mode only: remount the builder for a fresh widget. */
  onNewWidget: PropTypes.func,
};

export default WidgetPlayground;
